import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { PaymentApi } from "@/openapi/generated";
import type { ReleasePaymentRequest } from "@/openapi/generated";
import { createAuthenticatedApi } from "@/lib/utils";
import { toRequestCurrency } from "@/lib/utils/currency";
import { useToast } from "@/components/feedback";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import {
  invalidateAdminBalanceAfterPayment,
  scheduleAdminBalancePaymentRefetch,
} from "@/features/balance/utils/invalidate-member-balance-payment-queries";

export const usePaymentRelease = (toast: ReturnType<typeof useToast>) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { tenantId, tenantData } = useTenantData();

  return useMutation({
    mutationFn: async (releasePaymentRequest: ReleasePaymentRequest) => {
      if (!accessToken) throw new Error("Authentication token is required");
      const service = createAuthenticatedApi(PaymentApi, accessToken);
      await service.apiPaymentsReleasePost({
        releasePaymentRequest: {
          ...releasePaymentRequest,
          currency: toRequestCurrency(
            releasePaymentRequest.currency || tenantData?.currency,
          ),
        },
        xTenantID: tenantId,
      });
      return releasePaymentRequest;
    },
    onSuccess: async (request) => {
      const userId = Number(request.userId);
      await invalidateAdminBalanceAfterPayment(queryClient, {
        userId,
        studentId: Number(request.studentId),
      });
      scheduleAdminBalancePaymentRefetch(
        queryClient,
        userId,
        Number(request.studentId),
      );
      toast({
        title: "Payment released",
        description: "Your payment has been submitted and is being processed.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error) => {
      const message = await getApiErrorMessage(error, "Failed to release payment");
      toast({
        title: "Payment failed",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });
};
